// 📄 pages/Home/OrgHomeWidget.tsx
//
// "Home alineado a la organización activa" (2026-08-20, a pedido del
// usuario) — dos piezas, ambas condicionadas a la organización elegida en
// el OrgSwitcher de la topbar:
//   1. Resumen de la org (proyectos activos, miembros, alertas de casos de
//      prueba fallidos) — SOLO visible para owner/admin de esa org, porque
//      `listProjectsForOrg` (lectura amplia de TODOS los proyectos de la
//      org) lo exige así en firestore.rules (ver esOwnerOAdminDeOrg). Un
//      "Miembro" normal no tiene ese permiso — mismo tipo de gap que ya
//      causó el bug de MisProyectos.tsx (ver bug_fetch_proyectos_org_sin_permiso):
//      acá se gatea la FETCH con la misma condición desde el arranque, no
//      solo el render.
//   2. Accesos rápidos: proyectos fijados/recientes de ESTE usuario (no
//      depende del rol en la org — son proyectos donde ya es miembro real,
//      ver recentesLocalService.ts), con toggle de fijar y entrada directa.
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FolderKanban, AlertTriangle, Star, ArrowRight, RefreshCw } from 'lucide-react';
import { useAuth } from '../../context/AuthContext.tsx';
import TusOrganizaciones from './TusOrganizaciones.tsx';
import { listProjectsForOrg, getProject, Project } from '../../services/projectService.ts';
import { getCasosPrueba } from '../../services/casoPruebaService.ts';
import { getProyectosRecientes, getProyectosFijados, toggleProyectoFijado } from '../../services/recentesLocalService.ts';

const MAX_ACCESOS_RAPIDOS = 6;

function StatTile({ icon: Icon, valor, etiqueta }: { icon: typeof FolderKanban; valor: string | number; etiqueta: string }) {
  return (
    <div className="flex items-center gap-3 bg-white/5 border border-white/10 rounded-xl px-4 py-3 flex-1 min-w-[140px]">
      <Icon className="w-5 h-5 text-blue-400 flex-shrink-0" />
      <div>
        <p className="text-lg font-semibold text-white leading-none">{valor}</p>
        <p className="text-[11px] text-gray-400 mt-1">{etiqueta}</p>
      </div>
    </div>
  );
}

function ResumenOrganizacion({ orgId, puedeVer }: { orgId: string; puedeVer: boolean }) {
  const [loading, setLoading] = useState(true);
  const [proyectosActivos, setProyectosActivos] = useState(0);
  const [alertas, setAlertas] = useState(0);

  useEffect(() => {
    if (!puedeVer) { setLoading(false); return; }
    let cancelado = false;
    setLoading(true);
    listProjectsForOrg(orgId)
      .then(async (proyectos) => {
        const activos = proyectos.filter((p) => !p.archivado);
        if (cancelado) return;
        setProyectosActivos(activos.length);
        const listas = await Promise.all(activos.map((p) => getCasosPrueba(p.id).catch(() => [])));
        if (cancelado) return;
        setAlertas(listas.reduce((total, casos) => total + casos.filter((c) => c.estado === 'Fallido').length, 0));
      })
      .catch((err) => console.error('Error cargando resumen de la organización:', err))
      .finally(() => { if (!cancelado) setLoading(false); });
    return () => { cancelado = true; };
  }, [orgId, puedeVer]);

  if (!puedeVer) return null;

  return (
    <div className="flex flex-wrap gap-3 w-full max-w-4xl mb-6">
      {loading ? (
        <div className="flex items-center gap-2 text-sm text-gray-400 py-2">
          <RefreshCw className="w-4 h-4 animate-spin" /> Cargando resumen de la organización...
        </div>
      ) : (
        <>
          <StatTile icon={FolderKanban} valor={proyectosActivos} etiqueta="Proyectos activos" />
          <StatTile icon={AlertTriangle} valor={alertas} etiqueta="Casos de prueba fallidos" />
        </>
      )}
    </div>
  );
}

function TarjetaAccesoRapido({ project, fijado, onToggleFijar }: { project: Project; fijado: boolean; onToggleFijar: () => void }) {
  const navigate = useNavigate();
  return (
    <div
      onClick={() => navigate(`/proyecto/${project.id}`)}
      className="group relative bg-gray-800 border border-white/10 hover:border-blue-400 rounded-xl p-4 cursor-pointer transition-all duration-150 hover:-translate-y-0.5 w-56 flex-shrink-0"
    >
      <button
        onClick={(e) => { e.stopPropagation(); onToggleFijar(); }}
        title={fijado ? 'Quitar de fijados' : 'Fijar'}
        className="absolute top-3 right-3 text-gray-500 hover:text-amber-400 transition-colors"
      >
        <Star className={`w-4 h-4 ${fijado ? 'fill-amber-400 text-amber-400' : ''}`} />
      </button>
      <h3 className="text-sm font-semibold text-white pr-6 truncate">{project.nombre}</h3>
      <p className="text-xs text-gray-400 truncate mt-0.5">{project.clienteNombre || 'Sin cliente'}</p>
      <div className="flex items-center gap-1 text-[11px] text-blue-400 mt-3 opacity-0 group-hover:opacity-100 transition-opacity">
        Entrar <ArrowRight className="w-3 h-3" />
      </div>
    </div>
  );
}

function AccesosRapidos({ uid }: { uid: string }) {
  const [loading, setLoading] = useState(true);
  const [proyectos, setProyectos] = useState<Project[]>([]);
  const [fijados, setFijados] = useState<string[]>([]);

  const cargar = async () => {
    setLoading(true);
    const idsFijados = getProyectosFijados(uid);
    const idsRecientes = getProyectosRecientes(uid);
    const idsUnicos = [...idsFijados, ...idsRecientes.filter((id) => !idsFijados.includes(id))].slice(0, MAX_ACCESOS_RAPIDOS);
    const resultados = await Promise.all(idsUnicos.map((id) => getProject(id).catch(() => null)));
    setFijados(idsFijados);
    setProyectos(resultados.filter((p): p is Project => !!p));
    setLoading(false);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { cargar(); }, [uid]);

  const handleToggleFijar = (projectId: string) => {
    toggleProyectoFijado(uid, projectId);
    cargar();
  };

  if (loading || proyectos.length === 0) return null;

  return (
    <div className="w-full max-w-6xl mb-10">
      <div className="flex items-center gap-4 mb-4">
        <h2 className="text-lg font-semibold text-white/80">Accesos rápidos</h2>
        <div className="h-[1px] flex-grow bg-gradient-to-r from-white/20 to-transparent" />
      </div>
      <div className="flex gap-3 overflow-x-auto pb-2">
        {proyectos.map((p) => (
          <TarjetaAccesoRapido key={p.id} project={p} fijado={fijados.includes(p.id)} onToggleFijar={() => handleToggleFijar(p.id)} />
        ))}
      </div>
    </div>
  );
}

export default function OrgHomeWidget() {
  const { user, orgActual } = useAuth();

  if (!user || !orgActual) return null;

  const puedeVerResumen = orgActual.role === 'owner' || orgActual.role === 'admin';

  return (
    <>
      <TusOrganizaciones />
      <ResumenOrganizacion orgId={orgActual.id} puedeVer={puedeVerResumen} />
      <AccesosRapidos uid={user.uid} />
    </>
  );
}
